import { useQuery } from "@tanstack/react-query";

import { api } from "../api/rest";
import type { ShiftCell, ShiftSignal, ShiftSlice } from "../api/types";

/**
 * Такт опроса теневого журнала смещения.
 *
 * Тот же, что у соседних теневых журналов, и по той же причине: кнопок у
 * раздела нет, решений по нему не принимают, и секунда задержки ничего не стоит.
 */
const SHIFT_REFETCH_MS = 15_000;

/**
 * Окно сводки по умолчанию — НЕДЕЛЯ, а не сутки.
 *
 * Клеток здесь до полусотни (длительность × пять ступеней × пять минут), и
 * суточная выборка раскладывает по ним единицы строк, то есть отвечает шумом на
 * вопрос, заданный про долю. Здоровье подсистемы смотрят по нотам, а не здесь.
 */
export const SHIFT_DEFAULT_HOURS = 24 * 7;

/** Сводка смещения по клеткам «порог × минута». */
export function useShiftSummary(hours: number, windowLen = 0) {
  return useQuery<{ hours: number; cells: ShiftCell[] }>({
    queryKey: ["shift-summary", hours, windowLen],
    queryFn: () => api.shiftSummary(hours, windowLen),
    refetchInterval: SHIFT_REFETCH_MS,
    retry: 1,
  });
}

/**
 * Разрез клеток по «подтверждение расхождением × разворот» — на все клетки
 * одним запросом.
 *
 * Хук монтируется только под РАЗВЁРНУТОЙ строкой сводки, поэтому свёрнутая
 * панель разреза не опрашивает вовсе — тот же приём, что у тел FoldablePanel и
 * у разреза расхождения. Две развёрнутые строки складываются в один запрос
 * ключом TanStack.
 */
export function useShiftBreakdown(hours: number, windowLen = 0) {
  return useQuery<{ hours: number; confirm_s: number; slices: ShiftSlice[] }>({
    queryKey: ["shift-breakdown", hours, windowLen],
    queryFn: () => api.shiftBreakdown(hours, windowLen),
    refetchInterval: SHIFT_REFETCH_MS,
    retry: 1,
  });
}

/** Лента теневых входов, свежие сверху. */
export function useShiftSignals(hours: number, limit = 60, windowLen = 0) {
  return useQuery<{ signals: ShiftSignal[] }>({
    queryKey: ["shift-signals", hours, limit, windowLen],
    queryFn: () => api.shiftSignals(hours, undefined, limit, windowLen),
    refetchInterval: SHIFT_REFETCH_MS,
    retry: 1,
  });
}
